import React from "react";
import { Box, Button, Flex, Image, Text } from "@chakra-ui/react";
import SepCyan from "./Images/sep-cyan.svg";
import SepInk from "./Images/sep-ink.svg";
import DotCorrect from "./Images/dot-correct.svg";
import DotIncorrect from "./Images/dot-incorrect.svg";
import { formatExamBoard } from "../../Components/examBoards";
import EditableText from "./EditableText";
import { r, s } from "./reportTokens";

/** The dark metadata pill: Cambridge · IGCSE · 0580 (Figma 95:47). */
const BoardChip = ({ parts }) => (
  <Flex
    display="inline-flex"
    alignItems="center"
    gap={s(4)}
    bg={r.chip}
    borderRadius={s(63)}
    padding={`${s(4)} ${s(10)}`}
    width="fit-content"
  >
    {parts.map((part, index) => (
      <React.Fragment key={part}>
        {index > 0 && <Image src={SepCyan} alt="" width={s(4)} height={s(4)} />}
        <Text fontSize={s(11)} fontWeight="400" color={r.onChip} whiteSpace="nowrap">
          {part}
        </Text>
      </React.Fragment>
    ))}
  </Flex>
);

/** Strengths (95:73) and Main Gaps (96:82): same card, different marker. */
const FindingsCard = ({ icon, title, items, emptyLabel, editing, onChange, max }) => {
  const setItem = (index) => (value) =>
    onChange(items.map((item, i) => (i === index ? value : item)));
  const removeItem = (index) => () => onChange(items.filter((_, i) => i !== index));

  return (
    <Box
      className="report-block"
      flex="1"
      minWidth={0}
      bg={r.card}
      borderRadius={s(14)}
      padding={`${s(13)} ${s(16)} ${s(20)}`}
      minHeight={s(256)}
    >
      <Flex alignItems="center" gap={s(8)}>
        <Image src={icon} alt="" width={s(13)} height={s(13)} />
        <Text fontSize={s(14)} fontWeight="400" color={r.ink}>
          {title}
        </Text>
      </Flex>

      <Flex direction="column" gap={s(20)} marginTop={s(20)}>
        {items.length === 0 && !editing && (
          <Text fontSize={s(11)} color={r.muted}>
            {emptyLabel}
          </Text>
        )}
        {items.map((item, index) => (
          <Flex key={index} alignItems="flex-start" gap={s(7)}>
            <Flex
              flexShrink={0}
              alignItems="center"
              justifyContent="center"
              width={s(21)}
              height={s(20)}
              borderRadius={s(200)}
              bg={r.pillBg}
            >
              <Text fontSize={s(9)} fontWeight="400" color={r.ink}>
                {index + 1}
              </Text>
            </Flex>
            {editing ? (
              <Flex flex="1" minWidth={0} align="flex-start" gap={s(4)}>
                <EditableText value={item} onChange={setItem(index)} minHeight={s(40)} />
                <Button
                  className="report-edit-only"
                  onClick={removeItem(index)}
                  aria-label="Remove"
                  variant="unstyled"
                  minW="auto"
                  height={s(20)}
                  fontSize={s(13)}
                  color={r.muted}
                  _hover={{ color: "#d93025" }}
                >
                  ×
                </Button>
              </Flex>
            ) : (
              <Text fontSize={s(11)} fontWeight="400" color={r.ink} lineHeight="1.35">
                {item}
              </Text>
            )}
          </Flex>
        ))}

        {editing && items.length < max && (
          <Button
            className="report-edit-only"
            onClick={() => onChange([...items, ""])}
            variant="unstyled"
            alignSelf="flex-start"
            height={s(22)}
            fontSize={s(11)}
            fontWeight="400"
            color={r.editFocus}
          >
            + Add
          </Button>
        )}
      </Flex>
    </Box>
  );
};

const ReportSummary = ({ report, editing = false, draft, onDraft }) => {
  const strengths = (editing ? draft?.strengths : null) ?? report.strengths ?? [];
  const gaps = (editing ? draft?.gaps : null) ?? report.gaps ?? [];
  const chipParts = [
    formatExamBoard(report.exam_board_code),
    report.exam_qualification_code,
    report.subject_code,
  ].filter(Boolean);

  return (
    <>
      <Box marginTop={s(37)}>
        <BoardChip parts={chipParts} />
      </Box>

      {/* Not in the design, but a printed report handed to a parent has to say
          which paper it is about. */}
      {report.paper_name && (
        <Text fontSize={s(11)} color={r.muted} marginTop={s(8)}>
          {report.subject_name ? `${report.subject_name} · ` : ""}
          {report.paper_name}
        </Text>
      )}

      {/* Grade card (95:57) */}
      <Flex
        className="report-block"
        alignItems="center"
        gap={s(16)}
        bg={r.card}
        borderRadius={s(21)}
        minHeight={s(116)}
        padding={`${s(20)} ${s(24)} ${s(20)} ${s(19)}`}
        marginTop={s(14)}
      >
        <Flex
          flexShrink={0}
          alignItems="center"
          justifyContent="center"
          width={s(61)}
          height={s(61)}
          borderRadius={s(200)}
          bg={r.gradeBg}
          border={`${s(2)} solid`}
          borderColor={r.gradeBorder}
        >
          <Text fontSize={s(24)} fontWeight="500" color={r.gradeText} lineHeight="1">
            {report.grade}
          </Text>
        </Flex>

        <Box minWidth={0}>
          <Text fontSize={s(20)} fontWeight="500" color={r.ink} lineHeight="1.2">
            {report.student_name} Practice Exam Grade
          </Text>
          <Flex alignItems="center" gap={s(5)} marginTop={s(6)}>
            <Text fontSize={s(14)} fontStyle="italic" color="#000000">
              {report.scored_marks}/{report.total_marks}
            </Text>
            <Image src={SepInk} alt="" width={s(4)} height={s(4)} />
            <Text fontSize={s(14)} fontStyle="italic" color="#000000">
              {report.percentage}%
            </Text>
          </Flex>
        </Box>
      </Flex>

      <Flex gap={s(22)} marginTop={s(46)} align="stretch">
        <FindingsCard
          icon={DotCorrect}
          title="Strengths"
          items={strengths}
          emptyLabel="No full-mark questions on this paper."
          editing={editing}
          onChange={(next) => onDraft?.({ ...draft, strengths: next })}
          max={4}
        />
        <FindingsCard
          icon={DotIncorrect}
          title="Main Gaps"
          items={gaps}
          emptyLabel="Nothing lost on this paper."
          editing={editing}
          onChange={(next) => onDraft?.({ ...draft, gaps: next })}
          max={3}
        />
      </Flex>

      {/* Boundaries are not published per paper in our data, so the grade is a
          guide to how the mark reads, not a board award. Saying so on the sheet
          keeps the document honest. */}
      <Text fontSize={s(9)} color={r.muted} marginTop={s(16)} lineHeight="1.4">
        Grade shown is indicative, based on standard boundaries. Official
        boundaries are set per paper and per exam series.
      </Text>
    </>
  );
};

export default ReportSummary;
